import "bootstrap/dist/css/bootstrap.css";

import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { initWebVitals } from "./theaWeb/lib/webVitals";
import { initV4PreviewFlag } from "./theaWeb/lib/v4Preview";
import { initPreviewMemory } from "./theaWeb/lib/initPreviewMemory";
import { PrerenderStyleCleanup } from "./prerender/PrerenderStyleCleanup";
import { awaitPrerenderedRouteChunk } from "./prerender/awaitPrerenderedRouteChunk";
import { BrowserRouter } from "react-router-dom";
import { prepareSeoFallback, SeoFallbackRouteGuard } from "./theaWeb/seo/seoFallback";

// Read `?v4=1` / `?v4=0` and strip the param, at boot — BEFORE any lazy route
// chunk (board/guide/claim) can mount and miss it. See v4Preview.ts.
initV4PreviewFlag();

// Remember `?v1Preview=1` / `?giftGuideHub=1` (or forget them on `=0`) in this
// browser, so tester previews survive in-app links that don't carry the param.
// Boot, before any render — see lib/previewMemory.ts.
initPreviewMemory(window.location.search);

// `createRoot`, not `hydrateRoot`, even though `/`, `/privacy` and `/terms`
// now arrive with their body already rendered inside #root. There is nothing to
// hydrate: the prerendered markup is a readable snapshot for anything that does
// not run JavaScript, not a tree React is expected to adopt, and asking React to
// match it would turn every future divergence between the two renders into a
// console error for real users. React replaces #root's contents on its first
// commit, which is exactly the behaviour we want.
prepareSeoFallback();
const root = ReactDOM.createRoot(document.getElementById("root"));

// On /privacy and /terms, wait for the route's lazy chunk before taking over.
// Those two pages arrive with their full text already in the HTML, so the
// chunk-loading window is better spent showing that text than showing the
// Suspense shimmer in place of it. Resolves immediately on every other route,
// and on a timer if the chunk never arrives — it can only delay the takeover,
// never prevent it. See prerender/awaitPrerenderedRouteChunk.ts for the two
// alternatives this replaced and the measurements that ruled them out.
awaitPrerenderedRouteChunk(window.location.pathname).then(() => {
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <BrowserRouter>
          <SeoFallbackRouteGuard />
          {/* Drops the prerendered stylesheet at the commit that orphans it. */}
          <PrerenderStyleCleanup />
          <App />
        </BrowserRouter>
      </ErrorBoundary>
    </React.StrictMode>
  );
});

// Real-User Monitoring of Core Web Vitals (LCP, FID, CLS, FCP, TTFB) →
// GA4 `web_vitals` events. Replaces the old `reportWebVitals(console.log)`
// scaffold that came with create-react-app — same library, but data flows
// to the dashboard now instead of just devtools. See `lib/webVitals.ts`
// for the threshold scoring and the rationale.
initWebVitals();
